import { useEffect } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import Home from "@/pages/Home";
import Cars from "@/pages/Cars";
import CarDetail from "@/pages/CarDetail";
import Advisor from "@/pages/Advisor";
import Compare from "@/pages/Compare";
import News from "@/pages/News";
import NewsArticle from "@/pages/NewsArticle";
import Launches from "@/pages/Launches";
import Chat from "@/pages/Chat";
import Tools from "@/pages/Tools";
import { EMITool, OwnershipTool, EVReadinessTool, InsuranceTool, FuelVsEVTool, DealerDiscountTool } from "@/pages/tools/Calculators";
import { Login, Signup } from "@/pages/Auth";
import { Dashboard } from "@/pages/Dashboard";
import AdminApp from "@/pages/Admin";
import BestOf from "@/pages/BestOf";
import Brands from "@/pages/Brands";
import BrandDetail from "@/pages/BrandDetail";
import Reviews from "@/pages/Reviews";
import ReviewDetail from "@/pages/ReviewDetail";
import ComparisonPage from "@/pages/ComparisonPage";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function RoutedErrorBoundary({ children }) {
  const { pathname } = useLocation();
  // Reset the boundary on navigation so one broken page doesn't stick.
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
}

function Layout({ children }) {
  return (<div className="min-h-screen flex flex-col"><Navbar/><main className="flex-1">{children}</main><Footer/></div>);
}

function NotFound() {
  return (<div className="max-w-md mx-auto p-16 text-center" data-testid="not-found"><div className="font-display text-6xl tracking-tighter">404</div><div className="text-muted-foreground mt-2">This page doesn't exist.</div></div>);
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop/>
          <Layout>
            <RoutedErrorBoundary>
            <Routes>
              <Route path="/" element={<Home/>}/>
              <Route path="/cars" element={<Cars/>}/>
              <Route path="/cars/:slug" element={<CarDetail/>}/>
              <Route path="/advisor" element={<Advisor/>}/>
              <Route path="/compare" element={<Compare/>}/>
              <Route path="/news" element={<News/>}/>
              <Route path="/news/:slug" element={<NewsArticle/>}/>
              <Route path="/launches" element={<Launches/>}/>
              <Route path="/chat" element={<Chat/>}/>
              <Route path="/tools" element={<Tools/>}/>
              <Route path="/tools/emi" element={<EMITool/>}/>
              <Route path="/tools/ownership" element={<OwnershipTool/>}/>
              <Route path="/tools/ev-readiness" element={<EVReadinessTool/>}/>
              <Route path="/tools/insurance" element={<InsuranceTool/>}/>
              <Route path="/tools/fuel-vs-ev" element={<FuelVsEVTool/>}/>
              <Route path="/tools/dealer-discount" element={<DealerDiscountTool/>}/>
              <Route path="/login" element={<Login/>}/>
              <Route path="/signup" element={<Signup/>}/>
              <Route path="/dashboard" element={<Dashboard/>}/>
              <Route path="/admin/*" element={<AdminApp/>}/>
              <Route path="/best/:slug" element={<BestOf/>}/>
              <Route path="/brands" element={<Brands/>}/>
              <Route path="/brands/:slug" element={<BrandDetail/>}/>
              <Route path="/reviews" element={<Reviews/>}/>
              <Route path="/reviews/:slug" element={<ReviewDetail/>}/>
              <Route path="/compare-cars/:slug" element={<ComparisonPage/>}/>
              <Route path="*" element={<NotFound/>}/>
            </Routes>
            </RoutedErrorBoundary>
          </Layout>
          <Toaster richColors position="top-right"/>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
